import { useState } from 'react';
import { useCheckout, type PixPaymentResult } from '../../hooks/useCheckout';
import { usePricing } from '../../contexts/PricingContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { PixCheckoutModal } from './PixCheckoutModal';
import { useAuth } from '../../hooks/useAuth';

interface BuyButtonProps {
  productId: string;
  couponCode?: string;
  className?: string;
  label?: string;
  onSuccess?: () => void;
  onError?: (msg: string) => void;
  disabled?: boolean;
}

export function BuyButton({ productId, couponCode, className, label, onSuccess, onError, disabled }: BuyButtonProps) {
  const { formatProductPrice, isFree } = usePricing();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const { checkout, grantDirectAccess, startPixCheckout, state } = useCheckout({ onSuccess, onError });

  const [pixData, setPixData] = useState<PixPaymentResult | null>(null);
  const [pixLoading, setPixLoading] = useState(false);

  const free    = isFree(productId);
  const loading = state === 'loading';
  const isBrl   = currency === 'BRL';

  function handleClick() {
    if (free) {
      grantDirectAccess(productId);
    } else {
      checkout(productId, couponCode);
    }
  }

  async function handlePixClick() {
    if (!user) { onError?.('Faça login para continuar'); return; }
    setPixLoading(true);
    try {
      const result = await startPixCheckout(productId, couponCode);
      setPixData(result);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Erro ao gerar PIX');
    } finally {
      setPixLoading(false);
    }
  }

  function handlePixSuccess() {
    setPixData(null);
    onSuccess?.();
  }

  const priceText = free ? 'Acessar Gratuitamente' : `Comprar, ${formatProductPrice(productId)}`;

  return (
    <>
      <button
        className={className ?? 'btn btn-primary btn-block'}
        onClick={handleClick}
        disabled={disabled || loading}>
        {loading ? 'Processando...' : (label ?? (free ? '✦ Acessar Gratuitamente' : `✦ ${priceText}`))}
      </button>

      {!free && isBrl && (
        <button
          className="btn btn-secondary btn-block"
          style={{ marginTop: '0.5rem', fontSize: 'var(--text-caption)' }}
          onClick={handlePixClick}
          disabled={disabled || pixLoading || loading}>
          {pixLoading ? 'Gerando PIX...' : '🔑 Pagar com PIX'}
        </button>
      )}

      {pixData && (
        <PixCheckoutModal
          paymentId={pixData.paymentId}
          pixCode={pixData.pixCode}
          pixQrBase64={pixData.pixQrBase64}
          expiresAt={pixData.expiresAt}
          onSuccess={handlePixSuccess}
          onClose={() => setPixData(null)}
        />
      )}
    </>
  );
}
